import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load all pages except Index for code splitting
const Contact = lazy(() => import("./pages/Contact"));
const ZoneIntervention = lazy(() => import("./pages/ZoneIntervention"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Demoussage = lazy(() => import("./pages/services/Demoussage"));
const DemoussageMonuments = lazy(() => import("./pages/services/DemoussageMonuments"));
const Inspection = lazy(() => import("./pages/services/Inspection"));
const InspectionBatiments = lazy(() => import("./pages/services/InspectionBatiments"));
const NettoyageBardage = lazy(() => import("./pages/services/NettoyageBardage"));
const CartographieDrone = lazy(() => import("./pages/services/CartographieDrone"));
const NettoyageHauteur = lazy(() => import("./pages/services/NettoyageHauteur"));
const InspectionThermographique = lazy(() => import("./pages/services/InspectionThermographique"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const Rouen = lazy(() => import("./pages/Rouen"));
const LeHavre = lazy(() => import("./pages/LeHavre"));
const Dieppe = lazy(() => import("./pages/Dieppe"));
const SottevilleLesRouen = lazy(() => import("./pages/SottevilleLesRouen"));
const SaintEtienneDuRouvray = lazy(() => import("./pages/SaintEtienneDuRouvray"));
const LeGrandQuevilly = lazy(() => import("./pages/LeGrandQuevilly"));
const Fecamp = lazy(() => import("./pages/Fecamp"));
const Montivilliers = lazy(() => import("./pages/Montivilliers"));
const MontSaintAignan = lazy(() => import("./pages/MontSaintAignan"));
const Elbeuf = lazy(() => import("./pages/Elbeuf"));
const Bolbec = lazy(() => import("./pages/Bolbec"));
const Yvetot = lazy(() => import("./pages/Yvetot"));
const Barentin = lazy(() => import("./pages/Barentin"));
const Bihorel = lazy(() => import("./pages/Bihorel"));
const Oissel = lazy(() => import("./pages/Oissel"));
const LePetitQuevilly = lazy(() => import("./pages/LePetitQuevilly"));
const Canteleu = lazy(() => import("./pages/Canteleu"));
const Maromme = lazy(() => import("./pages/Maromme"));
const DevilleLesRouen = lazy(() => import("./pages/DevilleLesRouen"));
const BoisGuillaume = lazy(() => import("./pages/BoisGuillaume"));
const Lillebonne = lazy(() => import("./pages/Lillebonne"));
const Eu = lazy(() => import("./pages/Eu"));
const NeufchatelEnBray = lazy(() => import("./pages/NeufchatelEnBray"));
const Evreux = lazy(() => import("./pages/Evreux"));
const Vernon = lazy(() => import("./pages/Vernon"));
const Louviers = lazy(() => import("./pages/Louviers"));
const ValDeReuil = lazy(() => import("./pages/ValDeReuil"));
const Gisors = lazy(() => import("./pages/Gisors"));
const Bernay = lazy(() => import("./pages/Bernay"));
const PontAudemer = lazy(() => import("./pages/PontAudemer"));
const LesAndelys = lazy(() => import("./pages/LesAndelys"));
const Gaillon = lazy(() => import("./pages/Gaillon"));
const VerneuilAvreIton = lazy(() => import("./pages/VerneuilAvreIton"));
const Deauville = lazy(() => import("./pages/Deauville"));
const Honfleur = lazy(() => import("./pages/Honfleur"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/zone-intervention" element={<ZoneIntervention />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/services/demoussage" element={<Demoussage />} />
            <Route path="/services/demoussage-monuments" element={<DemoussageMonuments />} />
            <Route path="/services/inspection" element={<Inspection />} />
            <Route path="/services/inspection-batiments" element={<InspectionBatiments />} />
            <Route path="/services/nettoyage-bardage" element={<NettoyageBardage />} />
            <Route path="/services/cartographie-drone" element={<CartographieDrone />} />
            <Route path="/services/nettoyage-hauteur" element={<NettoyageHauteur />} />
            <Route path="/services/inspection-thermographique" element={<InspectionThermographique />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/rouen" element={<Rouen />} />
            <Route path="/le-havre" element={<LeHavre />} />
            <Route path="/dieppe" element={<Dieppe />} />
            <Route path="/sotteville-les-rouen" element={<SottevilleLesRouen />} />
            <Route path="/saint-etienne-du-rouvray" element={<SaintEtienneDuRouvray />} />
            <Route path="/le-grand-quevilly" element={<LeGrandQuevilly />} />
            <Route path="/fecamp" element={<Fecamp />} />
            <Route path="/montivilliers" element={<Montivilliers />} />
            <Route path="/mont-saint-aignan" element={<MontSaintAignan />} />
            <Route path="/elbeuf" element={<Elbeuf />} />
            <Route path="/bolbec" element={<Bolbec />} />
            <Route path="/yvetot" element={<Yvetot />} />
            <Route path="/barentin" element={<Barentin />} />
            <Route path="/bihorel" element={<Bihorel />} />
            <Route path="/oissel" element={<Oissel />} />
            <Route path="/le-petit-quevilly" element={<LePetitQuevilly />} />
            <Route path="/canteleu" element={<Canteleu />} />
            <Route path="/maromme" element={<Maromme />} />
            <Route path="/deville-les-rouen" element={<DevilleLesRouen />} />
            <Route path="/bois-guillaume" element={<BoisGuillaume />} />
            <Route path="/lillebonne" element={<Lillebonne />} />
            <Route path="/eu" element={<Eu />} />
            <Route path="/neufchatel-en-bray" element={<NeufchatelEnBray />} />
            <Route path="/evreux" element={<Evreux />} />
            <Route path="/vernon" element={<Vernon />} />
            <Route path="/louviers" element={<Louviers />} />
            <Route path="/val-de-reuil" element={<ValDeReuil />} />
            <Route path="/gisors" element={<Gisors />} />
            <Route path="/bernay" element={<Bernay />} />
            <Route path="/pont-audemer" element={<PontAudemer />} />
            <Route path="/les-andelys" element={<LesAndelys />} />
            <Route path="/gaillon" element={<Gaillon />} />
            <Route path="/verneuil-avre-iton" element={<VerneuilAvreIton />} />
            <Route path="/deauville" element={<Deauville />} />
            <Route path="/honfleur" element={<Honfleur />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
